import type {
  childPartV1,
  childPartV2,
  childPartV2Param,
} from "@/api/breakdownService";
import ChildPartColumn from "@/components/first-tier-supplier/breakdown/ChildPartColumn";
import ChildPartData from "@/components/first-tier-supplier/breakdown/ChildPartData";
import ChildPartDataEdit from "@/components/first-tier-supplier/breakdown/ChildPartDataEdit";
import ChildPartInfoColumn from "@/components/first-tier-supplier/breakdown/ChildPartInfoColumn";
import ChildPartInfoData from "@/components/first-tier-supplier/breakdown/ChildPartInfoData";
import ChildPartInfoDataEdit from "@/components/first-tier-supplier/breakdown/ChildPartInfoDataEdit";
import ChildPartProcessColumn from "@/components/first-tier-supplier/breakdown/ChildPartProcessColumn";
import ChildPartProcessData from "@/components/first-tier-supplier/breakdown/ChildPartProcessData";
import ChildPartProcessV2Column from "@/components/first-tier-supplier/breakdown/ChildPartProcessV2Column";
import ChildPartProcessV2Data from "@/components/first-tier-supplier/breakdown/ChildPartProcessV2Data";
import MaterialCostColumn from "@/components/first-tier-supplier/breakdown/MaterialCostColumn";
import MaterialCostData from "@/components/first-tier-supplier/breakdown/MaterialCostData";
import MaterialCostDataEdit from "@/components/first-tier-supplier/breakdown/MaterialCostDataEdit";
import {
  useGetBreakdownProcessV1,
  useGetBreakdownProcessV2,
} from "@/hooks/breakdownHooks";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Skeleton,
} from "@mui/material";
import { useEffect, useState } from "react";
import ChildPartProcessV2DataEdit from "../../components/first-tier-supplier/breakdown/ChildPartProcessV2DataEdit";

interface BreakdownV1TableProps {
  rows: Array<childPartV1 | childPartV2>;
  isLoading?: boolean;
  rowCount: number;
  page: number;
  rowsPerPage: number;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  supplierCode: string | undefined;
  isAdminPct: boolean;
  isProfitPct: boolean;
  partId?: number;
  partNo?: string;
  menu: string;
  version: "v1" | "v2";
  isAdding: boolean;
  isEditing: boolean;
  newChildPart: childPartV2Param | null;
  setNewChildPart: React.Dispatch<
    React.SetStateAction<childPartV2Param | null>
  >;
  selectedChildPartId: Array<number>;
  setSelectedChildPartId: React.Dispatch<React.SetStateAction<Array<number>>>;
  editedChildParts: Array<childPartV2Param>;
  setEditedChildParts: React.Dispatch<
    React.SetStateAction<Array<childPartV2Param>>
  >;
  validationErrors: Map<number, Set<string>>;
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const BreakdownTable = ({
  rows,
  isLoading = false,
  supplierCode,
  partId,
  partNo,
  rowCount,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  menu,
  version,
  isAdding,
  isEditing,
  newChildPart,
  setNewChildPart,
  isAdminPct,
  isProfitPct,
  selectedChildPartId,
  setSelectedChildPartId,
  editedChildParts,
  setEditedChildParts,
  validationErrors,
  setFlaggedReason,
}: BreakdownV1TableProps) => {
  const { data: breakdownProcessV1 } = useGetBreakdownProcessV1(
    supplierCode ? supplierCode : "",
  );
  const { data: breakdownProcessV2 } = useGetBreakdownProcessV2(
    partId ? partId : 0,
  );
  const [isOpenSecondTierProcessColumn, setIsOpenSecondTierProcessColumn] =
    useState<boolean>(false);
  const [isOpenOtherProcessColumn, setIsOpenOtherProcessColumn] =
    useState<boolean>(false);
  const [isOpenOEMProcessColumn, setIsOpenOEMProcessColumn] =
    useState<boolean>(false);
  const [isOpenSpareProcessColumn, setIsOpenSpareProcessColumn] =
    useState<boolean>(false);
  const [isOpenOEMPriceColumn, setIsOpenOEMPriceColumn] =
    useState<boolean>(false);
  const [isOpenSparePriceColumn, setIsOpenSparePriceColumn] =
    useState<boolean>(false);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const handleSelect = (row: childPartV2, checked: boolean) => {
    if (checked) {
      setSelectedChildPartId((prev) => [...prev, row.id]);
    } else {
      setSelectedChildPartId((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = rows?.map((row) => row.id) || [];
      setSelectedChildPartId(allIds);
    } else {
      setSelectedChildPartId([]);
    }
  };

  const handleUpdateField = (updatedPart: childPartV2Param) => {
    setEditedChildParts((prev) => {
      const existingIndex = prev.findIndex(
        (cp) => cp.child_part_v2_id === updatedPart.child_part_v2_id,
      );
      if (existingIndex !== -1) {
        const updatedParts = [...prev];
        updatedParts[existingIndex] = updatedPart as childPartV2Param;
        return updatedParts as childPartV2Param[];
      } else {
        return [...prev, updatedPart] as childPartV2Param[];
      }
    });
  };

  useEffect(() => {
    if (selectedChildPartId.length === rows?.length && rows?.length !== 0) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [selectedChildPartId, rows]);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          minHeight: "400px",
          overflow: "auto",
          position: "relative",
          width: "100%",
          backgroundColor: "#f3f3f3ff",
        }}
      >
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "auto" }}>
          <TableHead>
            <TableRow>
              {(() => {
                switch (menu) {
                  case "child-part":
                    return (
                      <>
                        <ChildPartColumn
                          version={version}
                          isAllSelected={isAllSelected}
                          handleSelectAll={handleSelectAll}
                          isAdding={isAdding}
                          isEditing={isEditing}
                        />
                        <ChildPartInfoColumn version={version} />
                      </>
                    );
                  case "material-cost":
                    return (
                      <>
                        <ChildPartColumn
                          version={version}
                          isAllSelected={isAllSelected}
                          handleSelectAll={handleSelectAll}
                          isAdding={isAdding}
                          isEditing={isEditing}
                        />
                        <MaterialCostColumn
                          isOpenSecondTierProcessColumn={
                            isOpenSecondTierProcessColumn
                          }
                          setIsOpenSecondTierProcessColumn={
                            setIsOpenSecondTierProcessColumn
                          }
                          secondTierProcessColumn={
                            version === "v1"
                              ? breakdownProcessV1?.second_tier_process || []
                              : breakdownProcessV2?.second_tier_process || []
                          }
                          isOpenOtherProcessColumn={isOpenOtherProcessColumn}
                          setIsOpenOtherProcessColumn={
                            setIsOpenOtherProcessColumn
                          }
                          otherProcessColumn={
                            version === "v1"
                              ? breakdownProcessV1?.other_process || []
                              : breakdownProcessV2?.other_process || []
                          }
                          isAdminPct={isAdminPct}
                          isProfitPct={isProfitPct}
                        />
                      </>
                    );
                  case "process":
                    return (
                      <>
                        <ChildPartColumn
                          version={version}
                          isAllSelected={isAllSelected}
                          handleSelectAll={handleSelectAll}
                          isAdding={isAdding}
                          isEditing={isEditing}
                        />
                        <ChildPartProcessColumn
                          isOpenOEMProcessColumn={isOpenOEMProcessColumn}
                          setIsOpenOEMProcessColumn={setIsOpenOEMProcessColumn}
                          isOpenSpareProcessColumn={isOpenSpareProcessColumn}
                          setIsOpenSpareProcessColumn={
                            setIsOpenSpareProcessColumn
                          }
                          isOpenOEMPriceColumn={isOpenOEMPriceColumn}
                          setIsOpenOEMPriceColumn={setIsOpenOEMPriceColumn}
                          isOpenSparePriceColumn={isOpenSparePriceColumn}
                          setIsOpenSparePriceColumn={setIsOpenSparePriceColumn}
                          OEMColumn={
                            version === "v1"
                              ? breakdownProcessV1?.oem_process || []
                              : breakdownProcessV2?.oem_process || []
                          }
                          spareColumn={
                            version === "v1"
                              ? breakdownProcessV1?.spare_process || []
                              : breakdownProcessV2?.spare_process || []
                          }
                        />
                      </>
                    );
                  case "child-part-process":
                    return (
                      <>
                        <ChildPartColumn
                          version={version}
                          isAllSelected={isAllSelected}
                          handleSelectAll={handleSelectAll}
                          isAdding={isAdding}
                          isEditing={isEditing}
                        />
                        <ChildPartProcessV2Column />
                      </>
                    );
                  default:
                    return null;
                }
              })()}
            </TableRow>
          </TableHead>
          <TableBody>
            {isAdding &&
              (() => {
                switch (menu) {
                  case "child-part":
                    return (
                      <TableRow key={0} sx={{ backgroundColor: "#fffbe6" }}>
                        <ChildPartDataEdit
                          partNo={partNo || ""}
                          newChildPart={newChildPart}
                          setNewChildPart={setNewChildPart}
                          validationErrors={
                            validationErrors.get(0) || new Set()
                          }
                        />
                        <ChildPartInfoDataEdit
                          newChildPart={newChildPart}
                          setNewChildPart={setNewChildPart}
                          supplierCode={supplierCode || ""}
                          validationErrors={
                            validationErrors.get(0) || new Set()
                          }
                          setFlaggedReason={setFlaggedReason}
                        />
                      </TableRow>
                    );
                  case "material-cost":
                    return (
                      <TableRow key={0} sx={{ backgroundColor: "#fffbe6" }}>
                        <ChildPartDataEdit
                          partNo={partNo || ""}
                          newChildPart={newChildPart}
                          setNewChildPart={setNewChildPart}
                          validationErrors={
                            validationErrors.get(0) || new Set()
                          }
                        />
                        <MaterialCostDataEdit
                          newChildPart={newChildPart}
                          setNewChildPart={setNewChildPart}
                          isOpenSecondTierProcessColumn={
                            isOpenSecondTierProcessColumn
                          }
                          secondTierProcessColumn={
                            version === "v1"
                              ? breakdownProcessV1?.second_tier_process || []
                              : breakdownProcessV2?.second_tier_process || []
                          }
                          isOpenOtherProcessColumn={isOpenOtherProcessColumn}
                          otherProcessColumn={
                            version === "v1"
                              ? breakdownProcessV1?.other_process || []
                              : breakdownProcessV2?.other_process || []
                          }
                          isAdminPct={isAdminPct}
                          isProfitPct={isProfitPct}
                          setFlaggedReason={setFlaggedReason}
                        />
                      </TableRow>
                    );
                  case "child-part-process":
                    return (
                      <ChildPartProcessV2DataEdit
                        partNo={partNo || ""}
                        childPartProcess={breakdownProcessV2?.cp_process || []}
                        newChildPart={newChildPart as childPartV2Param}
                        setNewChildPart={setNewChildPart}
                        validationErrors={validationErrors.get(0) || new Set()}
                        setFlaggedReason={setFlaggedReason}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            {rows?.length > 0 ? (
              rows?.map((row) => {
                if (menu === "child-part-process") {
                  if (isEditing && selectedChildPartId.includes(row.id)) {
                    return (
                      <ChildPartProcessV2DataEdit
                        key={row.id}
                        partNo={partNo || ""}
                        childPartProcess={breakdownProcessV2?.cp_process || []}
                        newChildPart={
                          editedChildParts.find(
                            (cp) => cp.child_part_v2_id === row.id,
                          ) || (newChildPart as childPartV2Param)
                        }
                        setNewChildPart={(updatedPart) => {
                          const currentPart =
                            editedChildParts.find(
                              (cp) => cp.child_part_v2_id === row.id,
                            ) || (newChildPart as childPartV2Param);
                          const newValue =
                            typeof updatedPart === "function"
                              ? updatedPart(currentPart)
                              : updatedPart;
                          if (newValue) {
                            handleUpdateField(newValue as childPartV2Param);
                          }
                        }}
                        validationErrors={validationErrors.get(0) || new Set()}
                        setFlaggedReason={setFlaggedReason}
                      />
                    );
                  } else {
                    return (
                      <ChildPartProcessV2Data
                        key={row.id}
                        version={version}
                        isSelected={selectedChildPartId.includes(row.id)}
                        onSelectChange={handleSelect}
                        row={row as childPartV2}
                        isAdding={isAdding}
                        isEditing={isEditing}
                      />
                    );
                  }
                }

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor:
                        isEditing && selectedChildPartId.includes(row.id)
                          ? "#fffbe6"
                          : "white",
                    }}
                  >
                    {(() => {
                      switch (menu) {
                        case "child-part": {
                          if (
                            isEditing &&
                            selectedChildPartId.includes(row.id)
                          ) {
                            return (
                              <>
                                <ChildPartDataEdit
                                  partNo={partNo || ""}
                                  newChildPart={
                                    editedChildParts.find(
                                      (cp) => cp.child_part_v2_id === row.id,
                                    ) || (newChildPart as childPartV2Param)
                                  }
                                  setNewChildPart={(updatedPart) => {
                                    const currentPart =
                                      editedChildParts.find(
                                        (cp) => cp.child_part_v2_id === row.id,
                                      ) || (newChildPart as childPartV2Param);

                                    const newValue =
                                      typeof updatedPart === "function"
                                        ? updatedPart(currentPart)
                                        : updatedPart;

                                    if (newValue) {
                                      handleUpdateField(
                                        newValue as childPartV2Param,
                                      );
                                    }
                                  }}
                                  validationErrors={
                                    validationErrors.get(row.id) || new Set()
                                  }
                                />
                                <ChildPartInfoDataEdit
                                  newChildPart={
                                    editedChildParts.find(
                                      (cp) => cp.child_part_v2_id === row.id,
                                    ) || (newChildPart as childPartV2Param)
                                  }
                                  setNewChildPart={(updatedPart) => {
                                    const currentPart =
                                      editedChildParts.find(
                                        (cp) => cp.child_part_v2_id === row.id,
                                      ) || (newChildPart as childPartV2Param);

                                    const newValue =
                                      typeof updatedPart === "function"
                                        ? updatedPart(currentPart)
                                        : updatedPart;

                                    if (newValue) {
                                      handleUpdateField(
                                        newValue as childPartV2Param,
                                      );
                                    }
                                  }}
                                  validationErrors={
                                    validationErrors.get(row.id) || new Set()
                                  }
                                  supplierCode={supplierCode || ""}
                                  setFlaggedReason={setFlaggedReason}
                                />
                              </>
                            );
                          } else {
                            return (
                              <>
                                <ChildPartData
                                  version={version}
                                  row={row}
                                  isSelected={selectedChildPartId.includes(
                                    row.id,
                                  )}
                                  onSelectChange={handleSelect}
                                  isAdding={isAdding}
                                  isEditing={isEditing}
                                />
                                <ChildPartInfoData row={row} />
                              </>
                            );
                          }
                        }
                        case "material-cost":
                          if (
                            isEditing &&
                            selectedChildPartId.includes(row.id)
                          ) {
                            return (
                              <>
                                <ChildPartDataEdit
                                  partNo={partNo || ""}
                                  newChildPart={
                                    editedChildParts.find(
                                      (cp) => cp.child_part_v2_id === row.id,
                                    ) || (newChildPart as childPartV2Param)
                                  }
                                  setNewChildPart={(updatedPart) => {
                                    const currentPart =
                                      editedChildParts.find(
                                        (cp) => cp.child_part_v2_id === row.id,
                                      ) || (newChildPart as childPartV2Param);

                                    const newValue =
                                      typeof updatedPart === "function"
                                        ? updatedPart(currentPart)
                                        : updatedPart;

                                    if (newValue) {
                                      handleUpdateField(
                                        newValue as childPartV2Param,
                                      );
                                    }
                                  }}
                                  validationErrors={
                                    validationErrors.get(row.id) || new Set()
                                  }
                                />
                                <MaterialCostDataEdit
                                  newChildPart={
                                    editedChildParts.find(
                                      (cp) => cp.child_part_v2_id === row.id,
                                    ) || (newChildPart as childPartV2Param)
                                  }
                                  setNewChildPart={(updatedPart) => {
                                    const currentPart =
                                      editedChildParts.find(
                                        (cp) => cp.child_part_v2_id === row.id,
                                      ) || (newChildPart as childPartV2Param);
                                    const newValue =
                                      typeof updatedPart === "function"
                                        ? updatedPart(currentPart)
                                        : updatedPart;
                                    if (newValue) {
                                      handleUpdateField(
                                        newValue as childPartV2Param,
                                      );
                                    }
                                  }}
                                  isOpenSecondTierProcessColumn={
                                    isOpenSecondTierProcessColumn
                                  }
                                  secondTierProcessColumn={
                                    version === "v1"
                                      ? breakdownProcessV1?.second_tier_process ||
                                        []
                                      : breakdownProcessV2?.second_tier_process ||
                                        []
                                  }
                                  isOpenOtherProcessColumn={
                                    isOpenOtherProcessColumn
                                  }
                                  otherProcessColumn={
                                    version === "v1"
                                      ? breakdownProcessV1?.other_process || []
                                      : breakdownProcessV2?.other_process || []
                                  }
                                  isAdminPct={isAdminPct}
                                  isProfitPct={isProfitPct}
                                  setFlaggedReason={setFlaggedReason}
                                />
                              </>
                            );
                          } else {
                            return (
                              <>
                                <ChildPartData
                                  version={version}
                                  row={row}
                                  isSelected={selectedChildPartId.includes(
                                    row.id,
                                  )}
                                  onSelectChange={handleSelect}
                                  isAdding={isAdding}
                                  isEditing={isEditing}
                                />
                                <MaterialCostData
                                  row={row}
                                  isOpenOtherProcessColumn={
                                    isOpenOtherProcessColumn
                                  }
                                  otherProcessColumn={
                                    version === "v1"
                                      ? breakdownProcessV1?.other_process || []
                                      : breakdownProcessV2?.other_process || []
                                  }
                                  isOpenSecondTierProcessColumn={
                                    isOpenSecondTierProcessColumn
                                  }
                                  secondTierProcessColumn={
                                    version === "v1"
                                      ? breakdownProcessV1?.second_tier_process ||
                                        []
                                      : breakdownProcessV2?.second_tier_process ||
                                        []
                                  }
                                />
                              </>
                            );
                          }
                        case "process":
                          return (
                            <>
                              <ChildPartData
                                version={version}
                                row={row}
                                isSelected={selectedChildPartId.includes(
                                  row.id,
                                )}
                                onSelectChange={handleSelect}
                                isAdding={isAdding}
                                isEditing={isEditing}
                              />
                              <ChildPartProcessData
                                row={row as childPartV1}
                                isOpenOEMProcessColumn={isOpenOEMProcessColumn}
                                OEMColumn={
                                  version === "v1"
                                    ? breakdownProcessV1?.oem_process || []
                                    : breakdownProcessV2?.oem_process || []
                                }
                                isOpenSpareProcessColumn={
                                  isOpenSpareProcessColumn
                                }
                                spareColumn={
                                  version === "v1"
                                    ? breakdownProcessV1?.spare_process || []
                                    : breakdownProcessV2?.spare_process || []
                                }
                                isOpenOEMPriceColumn={isOpenOEMPriceColumn}
                                isOpenSparePriceColumn={isOpenSparePriceColumn}
                              />
                            </>
                          );
                        default:
                          return null;
                      }
                    })()}
                  </TableRow>
                );
              })
            ) : isLoading ? (
              Array.from({ length: Math.min(rowsPerPage, 8) }).map((_, i) => (
                <TableRow key={i} sx={{ backgroundColor: "white" }}>
                  <TableCell
                    sx={{
                      width: 70,
                      minWidth: 70,
                      py: 1.5,
                      textAlign: "center",
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={20}
                      height={20}
                      sx={{ mx: "auto" }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 150, minWidth: 150, py: 1.5 }}>
                    <Skeleton
                      variant="text"
                      width="75%"
                      sx={{ fontSize: "0.875rem" }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 150, minWidth: 150, py: 1.5 }}>
                    <Skeleton
                      variant="text"
                      width="85%"
                      sx={{ fontSize: "0.875rem" }}
                    />
                  </TableCell>
                  {[90, 70, 80, 65, 75, 70].map((w, j) => (
                    <TableCell key={j} sx={{ py: 1.5, minWidth: 100 }}>
                      <Skeleton
                        variant="text"
                        width={`${w}%`}
                        sx={{ fontSize: "0.875rem" }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={100}
                  align="center"
                  sx={{ py: 3, height: "300px" }}
                >
                  ไม่มีข้อมูล
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rowCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
};

export default BreakdownTable;
